import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Truck, 
  Calendar, 
  Package, 
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Wrench,
  Battery,
  TrendingUp,
  Plus,
  Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLogistique = () => {
  const [stats, setStats] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [statsResponse, clientsResponse] = await Promise.all([
        axios.get(`${API}/dashboard/logistique`),
        axios.get(`${API}/clients`)
      ]);

      setStats(statsResponse.data);
      // Filtrer les clients avec des installations
      setInstallations(clientsResponse.data.filter(c => c.status === 'client' || c.status === 'installe'));
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard logistique:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            Dashboard Logistique
          </h1>
          <p className="text-gray-600">
            Planning installations, gestion stock et suivi chantiers
          </p>
        </div>
        <div className="mt-4 lg:mt-0 flex gap-3">
          <button className="btn btn-secondary">
            <Calendar className="w-4 h-4" />
            Planning
          </button>
          <button className="btn btn-primary">
            <Plus className="w-4 h-4" />
            Nouvelle installation
          </button>
        </div>
      </div>

      {/* Stats Cards Logistique */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="stats-card slide-up bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-green-600">{stats?.installations_planifiees || 0}</div>
              <div className="stats-label text-green-800">Planifiées</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-blue-600">{stats?.installations_en_cours || 0}</div>
              <div className="stats-label text-blue-800">En cours</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <Wrench className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-purple-600">{stats?.stock_panneaux || 0}</div>
              <div className="stats-label text-purple-800">Panneaux stock</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="stats-card slide-up bg-gradient-to-br from-orange-50 to-red-50 border-orange-200" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center justify-between">
            <div>
              <div className="stats-value text-orange-600">{stats?.equippes_disponibles || 0}</div>
              <div className="stats-label text-orange-800">Équipes dispo</div>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Performance & Planning */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Performance Logistique</h2>
              </div>
              <span className="text-sm text-gray-500">Ce mois</span>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Taux de Completion</span>
                  <span className="text-lg font-bold text-success">{stats?.taux_completion?.toFixed(1) || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-success to-green-600 h-3 rounded-full transition-all duration-1000" 
                    style={{ width: `${Math.min(stats?.taux_completion || 0, 100)}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">{stats?.installations_en_cours || 0}</div>
                  <div className="text-xs text-gray-600">Actives</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-orange-600">{stats?.chantiers_retard || 0}</div>
                  <div className="text-xs text-gray-600">En retard</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">{stats?.equippes_disponibles || 0}</div>
                  <div className="text-xs text-gray-600">Équipes</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-purple-500" />
              <h2 className="text-lg font-semibold text-gray-900">Gestion Stock</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Battery className="w-5 h-5 text-purple-600" />
                  <div>
                    <div className="font-medium text-purple-900">Panneaux 400W</div>
                    <div className="text-sm text-purple-700">{stats?.stock_panneaux || 0} unités</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-purple-600">En stock</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <Wrench className="w-5 h-5 text-blue-600" />
                  <div>
                    <div className="font-medium text-blue-900">Onduleurs</div>
                    <div className="text-sm text-blue-700">85 unités</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-blue-600">Disponible</div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <div>
                    <div className="font-medium text-orange-900">Supports</div>
                    <div className="text-sm text-orange-700">12 kits</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold text-orange-600">Stock bas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Planning des Installations */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Planning des Installations</h2>
            </div>
            <button className="text-sm text-primary hover:text-primary-dark font-medium">
              Voir planning complet
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {installations.length > 0 ? (
            <div className="space-y-0">
              {installations.slice(0, 5).map((installation, index) => {
                const isInstalled = installation.status === 'installe';
                const statusConfig = isInstalled 
                  ? { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'Terminée' }
                  : { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Planifiée' };
                
                const StatusIcon = statusConfig.icon;
                
                return (
                  <div key={installation.id} className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 ${statusConfig.bg} rounded-xl flex items-center justify-center`}>
                          <StatusIcon className={`w-6 h-6 ${statusConfig.color}`} />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">
                            {installation.prenom} {installation.nom}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {installation.ville}
                            </span>
                            <span className="flex items-center gap-1">
                              <Battery className="w-3 h-3" />
                              {installation.surface_toit || 'N/A'}m²
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className={`text-sm font-medium ${statusConfig.color}`}>
                            {statusConfig.label}
                          </div>
                          <div className="text-xs text-gray-500">
                            {isInstalled ? 'Terminée' : 'À planifier'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="btn btn-secondary btn-sm">
                            <Eye className="w-3 h-3" />
                          </button>
                          <button className="btn btn-primary btn-sm">
                            <Calendar className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucune installation planifiée</p>
            </div>
          )}
        </div>
      </div>

      {/* Alertes Logistique */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-gray-900">Alertes Stock</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-sm font-medium text-orange-900">Supports toiture - Stock bas</span>
                </div>
                <span className="text-xs text-orange-700">12 unités</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm font-medium text-yellow-900">Câbles DC - À commander</span>
                </div>
                <span className="text-xs text-yellow-700">250m</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Planification</h2>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-sm font-medium text-blue-900">Prochaine installation</span>
                </div>
                <span className="text-xs text-blue-700">Demain 9h</span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-900">Équipes disponibles</span>
                </div>
                <span className="text-xs text-green-700">3 équipes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLogistique;